import {Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {DocumentService} from '@services/document.service';
import {FileCheckService} from '@services/file-check.service';
import Required from '@util/decorators/required';
import {UploadDocument} from '@services/resource/upload-document';
import {UploadDocumentType} from '@services/resource/upload-document-type';
import {combineLatest, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {ContinuousActionComponent} from '@widgets/continuous-action/continuous-action.component';
import {ClientConfig} from '@app/client-config';
import {MarkantElementsModule} from '@markant/elements-angular';
import {MatDivider} from '@angular/material/divider';
import {DropzoneComponent} from '@dashboard/dropzone/dropzone.component';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'app-upload-document',
  templateUrl: './upload-document.component.html',
  styleUrls: ['./upload-document.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadDocumentComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [CommonModule, MarkantElementsModule, MatDivider, ContinuousActionComponent, DropzoneComponent],
})
export class UploadDocumentComponent implements OnInit {
  @Required @Input() productId: string;
  @Output() documentListChanged = new EventEmitter<UploadDocument[]>();

  @Input() documents: UploadDocument[];
  @Input() disabled = false;

  @ViewChild(ContinuousActionComponent, {static: true})
  continuous: ContinuousActionComponent;

  private readonly newFileList$: Subject<FileList> = new Subject();

  public readonly maxFileSize = ClientConfig.maxDocumentSize;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly documentService: DocumentService,
    private readonly fileCheckService: FileCheckService,
  ) {}

  ngOnInit(): void {
    this.documents = this.documents || [];

    this.newFileList$
      .pipe(
        map((fl) => Array.from(fl)),
        map((fl) => this.validateFiles(fl)),
        this.continuous.actions((fs) => combineLatest(fs.map((f) => this.documentService.create(f, UploadDocumentType.LIST, this.productId)))),
      )
      .subscribe(
        (docs: UploadDocument[]) => {
          docs.forEach((d) => this.documents.push(d));
          this.documentListChanged.emit(this.documents);
        },
        () => this.snackBar.open('Netzwerkfehler beim Hochladen', 'Schließen', {duration: 6000}),
      );
  }

  handleNewFiles(fileList: FileList) {
    if (fileList.length > 0) {
      this.newFileList$.next(fileList);
    }
  }

  /* returns the valid files and prints error messages for the invalid ones */
  private validateFiles(fs: File[]): File[] {
    return fs.filter((f) => {
      const errors = this.fileCheckService.checkFile(f, ['application/pdf'], ClientConfig.maxDocumentSize);
      errors.forEach((e) => this.snackBar.open(e.message, 'Schließen', {duration: 6000}));
      return errors.length === 0;
    });
  }

  removeFile(idx: number) {
    if (this.disabled) {
      return;
    }
    this.documents.splice(idx, 1);
    this.documentListChanged.emit(this.documents);
  }
}
